import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useSupervisor } from '@/hooks/useSupervisor';
import type { Order } from '@/types/supervisor';
import { getPrinters } from '@/services/productService';
import { logging } from '@/services/logging';
import { selectOrderItems, updateOrderItem } from '@/features/order/orderSlice';
import { sanitizeOrderItems } from '@/features/order/utils/canonicalOrder';
import { supervisorService } from '@/services/supervisorService';
import SetPaperTypeWindow from './SetPaperTypeWindow';
import ItemStepPanelLayout from './ItemStepPanelLayout';
import { autoAssignSinglePrinter } from './utils/autoAssignSinglePrinter';
import { usePrintableItemTargets, useSelectionGuard } from './hooks/usePrintableItemTargets';

interface PaperStepPanelProps {
  readonly order: Order;
  readonly onUpdatePaperType?: (itemId: string, paperTypeId: string) => void;
}

const PaperStepPanel = ({ order, onUpdatePaperType }: PaperStepPanelProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const orderItems = useAppSelector(selectOrderItems);
  const { send: sendToSupervisor } = useSupervisor();

  const { printableItems, targetedItems, hasSelectedItems } = usePrintableItemTargets(order);

  useSelectionGuard({
    printableItems,
    hasSelectedItems,
    send: sendToSupervisor,
    aiPrompt: 'Politely explain that the user needs to select at least one uploaded image before choosing a paper type.',
    fallbackMessage: t(
      'uploadedImages.aiFallback.paper.noSelection',
      'Select at least one image to choose paper.',
    ),
  });

  const handleExit = () => {
    sendToSupervisor({ type: 'UI.GOTO_STEP', step: 'uploadedImages' });
  };

  const canApplyToAll = targetedItems.length > 1;

  const handleSave = useCallback((itemId: string, paperTypeId: string) => {
    if (onUpdatePaperType) {
      onUpdatePaperType(itemId, paperTypeId);
    }
  }, [onUpdatePaperType]);

  const handlePersistComplete = useCallback((savedItems: ReadonlyArray<{ itemId: string; value: string }>) => {
    if (!Array.isArray(savedItems) || savedItems.length === 0) {
      return;
    }

    void (async () => {
      try {
        const savedPaperByItemId = new Map(savedItems.map(({ itemId, value }) => [itemId, value] as const));
        const currentSupervisorItems = supervisorService.getSnapshot().context.order.items ?? [];
        const sourceItems = currentSupervisorItems.length > 0
          ? currentSupervisorItems
          : (orderItems.length > 0 ? orderItems : (order.items ?? []));

        await autoAssignSinglePrinter({
          getPrinters,
          eligibleIds: new Set(savedItems.map(({ itemId }) => itemId)),
          items: sourceItems,
          onAssign: async (idsToUpdate, singlePrinterId) => {
            idsToUpdate.forEach(orderItemId => {
              dispatch(updateOrderItem({
                orderItemId,
                data: { printerId: singlePrinterId },
              }));
            });

            const latestSupervisorItems = supervisorService.getSnapshot().context.order.items ?? [];
            const patchSource = latestSupervisorItems.length > 0 ? latestSupervisorItems : sourceItems;
            const updateSet = new Set(idsToUpdate);
            const patchSourceItems = patchSource as Array<Record<string, unknown> & {
              id?: string;
              printerId?: string | null;
              paperTypeId?: string | null;
            }>;
            const patchedItems = patchSourceItems.map(entry => {
              if (!entry || !entry.id) {
                return entry;
              }

              const preservedPaperId = savedPaperByItemId.get(entry.id);
              const shouldAssignPrinter = updateSet.has(entry.id) && !entry.printerId;
              if (!preservedPaperId && !shouldAssignPrinter) {
                return entry;
              }

              return {
                ...entry,
                ...(preservedPaperId ? { paperTypeId: preservedPaperId } : {}),
                ...(shouldAssignPrinter ? { printerId: singlePrinterId } : {}),
              };
            });

            sendToSupervisor({
              type: 'ORDER.APPLY_PATCH',
              patch: { items: sanitizeOrderItems(patchedItems) },
            });

          },
        });
      } catch (error) {
        logging.error('[PaperStepPanel] Failed to auto-assign single printer after paper save', { error });
      }
    })();
  }, [dispatch, order.items, orderItems, sendToSupervisor]);

  return (
    <ItemStepPanelLayout<string>
      t={t}
      availableItems={printableItems}
      items={targetedItems}
      title={t('canvas.steps.selectItemForPaper', 'Choose item for paper selection')}
      emptyStateMessage={t('setPaperType.noItem', 'Select an item to choose its paper type.')}
      uploadCtaLabel={t('orderSummary.goToUpload', 'Upload images')}
      onUploadClick={() => sendToSupervisor({ type: 'UI.GOTO_STEP', step: 'upload' })}
      onExit={handleExit}
      enableUnsavedExitGuard
      pristineSaveLabel={t('setPaperType.confirmCta', 'Confirm paper type')}
      dirtySaveLabel={t('setPaperType.saveCta', 'Save paper type')}
      discardChangesTitle={t('setPaperType.discardChangesTitle', 'Discard unsaved paper changes?')}
      discardChangesDescription={t('setPaperType.discardChangesDescription', 'Your paper selection is not saved yet. You can keep editing or discard it and leave this screen.')}
      discardChangesConfirmText={t('setPaperType.discardChangesConfirm', 'Discard changes')}
      discardChangesCancelText={t('setPaperType.discardChangesCancel', 'Keep editing')}
      resolveItemValue={(item) => item.paperTypeId ?? undefined}
      isItemAssigned={(entry) => Boolean(entry?.paperTypeId)}
      onSave={handleSave}
      onPersistComplete={handlePersistComplete}
      renderWindow={({ item, onClose, onBack, onSave, onConfirmSelection, draftValue, setDraftValue, clearDraftValue, setDraftValueForAll, saveLabel }) => (
        <SetPaperTypeWindow
          key={item.id}
          item={item}
          onClose={onClose}
          onBack={onBack}
          saveLabel={saveLabel}
          onSave={onSave}
          onConfirmSelection={onConfirmSelection}
          draftPaperTypeId={draftValue ?? undefined}
          onDraftChange={(paperTypeId) => {
            if (paperTypeId === null) {
              setDraftValue(undefined);
              return;
            }
            setDraftValue(paperTypeId);
          }}
          onDraftReset={() => clearDraftValue()}
          onApplyPaperToAll={canApplyToAll
            ? async (paperTypeId) => {
                setDraftValue(paperTypeId);
                setDraftValueForAll(paperTypeId);
              }
            : undefined}
        />
      )}
    />
  );
};

export default PaperStepPanel;
